import { FC, HTMLAttributes, PropsWithChildren } from 'react'
import styles from './button.module.scss'
import classNames from '@/utils/helpers/class-names.helper'
import Link from 'next/link'
import useI18nPath from '@/hooks/i18n-path.hook'

type ButtonProps = HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> & {
  as?: 'button' | 'a'
  className?: string
  icon?: React.ReactNode
  iconClassName?: string
  iconPosition?: 'left' | 'right'
  main?: boolean
  dark?: boolean
  light?: boolean
  variant?: 'primary' | 'secondary'
  href?: string
  type?: 'button' | 'submit' | 'reset'
  size?: '_lg'
  target?: '_blank'
  disabled?: boolean
}

const Button: FC<PropsWithChildren<ButtonProps>> = ({
  as = 'button',
  iconPosition = 'left',
  className,
  children,
  icon,
  iconClassName,
  main = false,
  type = 'button',
  disabled,
  dark,
  light,
  size,
  variant,
  target,
  ...props
}) => {
  const { getHrefLang } = useI18nPath()

  const buttonProps = {
    ...props,
    className: classNames(
      styles.button,
      'nsc',
      main ? `${styles._main} _main ${styles['glow-on-hover']}` : '',
      dark ? `${styles._dark} _dark` : '',
      light ? `${styles._light} _light` : '',
      size ? `${styles[size]} ${size}` : '',
      variant ? `${styles[`_${variant}`]} _${variant}` : '',
      className,
    ),
  }

  const ButtonContent = (
    <>
      {icon ? (
        <>
          {iconPosition === 'left' && (
            <span className={classNames(styles.button__icon, iconClassName)}>{icon}</span>
          )}
          {children && <span className={styles.button__text}>{children}</span>}
          {iconPosition === 'right' && (
            <span className={classNames(styles.button__icon, iconClassName)}>{icon}</span>
          )}
        </>
      ) : (
        children
      )}
    </>
  )

  if (as === 'a') {
    return (
      <Link {...buttonProps} href={getHrefLang(props.href || '#')} target={target}>
        {ButtonContent}
      </Link>
    )
  }
  return (
    <button type={type} {...buttonProps} disabled={disabled}>
      {ButtonContent}
    </button>
  )
}

export default Button
